import React, { Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth, useActor } from '@/app/auth';
import { preloadOrgLookups } from '@/app/lookups';
import { preloadAllAppData } from '@/app/data-cache';
import { usePlatformBranding } from '@/app/platform-branding';
import { useTheme } from '@/app/theme';
import { AppLayout } from '@/components/AppLayout';
import { BrandIcon } from '@/components/BrandIcon';
import { Icon } from '@/components/Icons';
import { Button, DashboardSkeleton } from '@/components/ui';
import type { PermissionKey } from '@/domain/permissions';
import { TASKFLOW_PLATFORM_THEME } from '@/app/platform-theme-presets';

export type PreloadableComponent<P = any> = React.ComponentType<P> & {
  preload: () => Promise<any>;
};

function lazyWithPreload<P = any>(
  factory: () => Promise<any>,
  namedExport?: string,
): PreloadableComponent<P> {
  let factoryPromise: Promise<{ default: React.ComponentType<P> }> | null = null;
  const load = () => {
    if (!factoryPromise) {
      factoryPromise = factory().then((mod) => ({
        default: (namedExport ? mod[namedExport] : (mod.default ?? mod)) as React.ComponentType<P>,
      }));
    }
    return factoryPromise;
  };

  const LazyComponent = React.lazy(load) as any;
  LazyComponent.preload = load;
  return LazyComponent as PreloadableComponent<P>;
}

// ---------------------------------------------------------------------------
// Lazy pages with instant background preloading capability
// ---------------------------------------------------------------------------
const LoginPage = lazyWithPreload(() => import('@/pages/auth-pages'), 'LoginPage');
const SignUpPage = lazyWithPreload(() => import('@/pages/auth-pages'), 'SignUpPage');
const ForgotPasswordPage = lazyWithPreload(() => import('@/pages/auth-pages'), 'ForgotPasswordPage');
const AuthCallbackPage = lazyWithPreload(() => import('@/pages/auth-pages'), 'AuthCallbackPage');
const BootstrapPage = lazyWithPreload(() => import('@/pages/auth-pages'), 'BootstrapPage');

const RegisterPage = lazyWithPreload(() => import('@/pages/register-pages'), 'RegisterPage');
const RegisterCompletePage = lazyWithPreload(() => import('@/pages/register-pages'), 'RegisterCompletePage');
const InviteAcceptPage = lazyWithPreload(() => import('@/pages/register-pages'), 'InviteAcceptPage');

import { DashboardPage } from '@/pages/dashboard-page';
const TasksListPage = lazyWithPreload<{ view?: string }>(() => import('@/pages/tasks-list-page'), 'TasksListPage');
const TaskFormPage = lazyWithPreload(() => import('@/pages/task-form-page'), 'TaskFormPage');
const TaskDetailsPage = lazyWithPreload(() => import('@/pages/task-details-page'), 'TaskDetailsPage');
const TemplatesPage = lazyWithPreload(() => import('@/pages/templates-page'), 'TemplatesPage');
const TemplateFormPage = lazyWithPreload(() => import('@/pages/template-form-page'), 'TemplateFormPage');
const NotificationsPage = lazyWithPreload(() => import('@/pages/notifications-page'), 'NotificationsPage');
const ReportsPage = lazyWithPreload(() => import('@/pages/reports-page'), 'ReportsPage');
const ProfilePage = lazyWithPreload(() => import('@/pages/profile-page'), 'ProfilePage');
const CompanyProfilePage = lazyWithPreload(() => import('@/pages/company-profile-page'), 'CompanyProfilePage');
const SettingsPage = lazyWithPreload<{ scope?: string }>(() => import('@/pages/settings-page'), 'SettingsPage');
const WorkspacePage = lazyWithPreload(() => import('@/pages/workspace-page'), 'WorkspacePage');

const CompaniesPage = lazyWithPreload(() => import('@/pages/admin/companies-page'), 'CompaniesPage');
const CompanySettingsPage = lazyWithPreload(() => import('@/pages/admin/company-settings-page'), 'CompanySettingsPage');
const StructurePage = lazyWithPreload<{ initialTab?: string }>(() => import('@/pages/admin/structure-page'), 'StructurePage');
const PlatformHomePage = lazyWithPreload(() => import('@/pages/platform/platform-home-page'), 'PlatformHomePage');
const PlatformSettingsPage = lazyWithPreload(() => import('@/pages/platform/platform-settings-page'), 'PlatformSettingsPage');
const PlatformBillingPage = lazyWithPreload(() => import('@/pages/platform/platform-billing-page'), 'PlatformBillingPage');
const PlatformMembersPage = lazyWithPreload(() => import('@/pages/platform/platform-members-page'), 'PlatformMembersPage');
const PlatformActivityPage = lazyWithPreload(() => import('@/pages/platform/platform-activity-page'), 'PlatformActivityPage');
const UsersPage = lazyWithPreload(() => import('@/pages/admin/users-page'), 'UsersPage');
const RolesPage = lazyWithPreload(() => import('@/pages/admin/roles-page'), 'RolesPage');
const RoleEditorPage = lazyWithPreload(() => import('@/pages/admin/roles-page'), 'RoleEditorPage');
const SupervisionPage = lazyWithPreload(() => import('@/pages/admin/supervision-page'), 'SupervisionPage');
const CategoriesPage = lazyWithPreload(() => import('@/pages/admin/categories-page'), 'CategoriesPage');
const ThemingPage = lazyWithPreload(() => import('@/pages/admin/theming-page'), 'ThemingPage');
const RetentionPage = lazyWithPreload(() => import('@/pages/admin/retention-page'), 'RetentionPage');
const AuditPage = lazyWithPreload(() => import('@/pages/admin/audit-page'), 'AuditPage');

const ChatPage = lazyWithPreload(() => import('@/pages/chat-page'), 'ChatPage');
const BulletinsPage = lazyWithPreload(() => import('@/pages/bulletins-page'), 'BulletinsPage');
const MediaHubPage = lazyWithPreload(() => import('@/pages/media-hub-page'), 'MediaHubPage');
const ShiftHandoverPage = lazyWithPreload(() => import('@/pages/shift-handover-page'), 'ShiftHandoverPage');
const StoreManualPage = lazyWithPreload(() => import('@/pages/store-manual-page'), 'StoreManualPage');
const StaffRolesPage = lazyWithPreload(() => import('@/pages/staff-roles-page'), 'StaffRolesPage');

const LeaderboardPage = lazyWithPreload(() => import('@/pages/leaderboard-page'), 'LeaderboardPage');
const AssetsPage = lazyWithPreload(() => import('@/pages/assets-page'), 'AssetsPage');
const OnboardingPage = lazyWithPreload(() => import('@/pages/onboarding-page'), 'OnboardingPage');
const AutomationsPage = lazyWithPreload(() => import('@/pages/automations-page'), 'AutomationsPage');
const AboutPage = lazyWithPreload(() => import('@/pages/about-page'), 'AboutPage');

// All screens ordered by user priority for sequential background preloading
const ALL_APP_SCREENS: PreloadableComponent<any>[] = [
  // Core daily screens
  TasksListPage,
  StoreManualPage,
  StaffRolesPage,
  ChatPage,
  LeaderboardPage,
  NotificationsPage,
  AssetsPage,
  OnboardingPage,
  AutomationsPage,
  AboutPage,
  TaskDetailsPage,
  TaskFormPage,
  ReportsPage,
  ShiftHandoverPage,
  MediaHubPage,
  BulletinsPage,
  CompanyProfilePage,
  SettingsPage,
  WorkspacePage,
  // Templates
  TemplatesPage,
  TemplateFormPage,
  // Admin screens
  StructurePage,
  CategoriesPage,
  UsersPage,
  RolesPage,
  RoleEditorPage,
  CompanySettingsPage,
  ThemingPage,
  SupervisionPage,
  RetentionPage,
  AuditPage,
  // Platform screens
  PlatformHomePage,
  CompaniesPage,
  PlatformMembersPage,
  PlatformSettingsPage,
  PlatformBillingPage,
  PlatformActivityPage,
  ProfilePage,
];

function scheduleIdleWork(cb: () => void, timeoutMs: number) {
  if (typeof window === 'undefined') return;
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(cb, { timeout: timeoutMs });
  } else {
    setTimeout(cb, timeoutMs);
  }
}

let preloadStarted = false;

function triggerAutoPreloadAllScreens() {
  if (preloadStarted || typeof window === 'undefined') return;
  preloadStarted = true;

  // 1. الدفعة الأولى ذات الأولوية القصوى (الشاشات اليومية الأكثر استخدامًا) تُحمَّل فورًا وبالتوازي
  const priorityScreens = ALL_APP_SCREENS.slice(0, 10);
  for (const screen of priorityScreens) {
    try {
      void screen.preload();
    } catch {
      // Ignore background preload failures
    }
  }

  // 2. باقي شاشات النظام تُحمَّل بدفعات سريعة متتالية دون إبطاء لكي تكون جاهزة في الذاكرة فورًا
  const secondaryScreens = ALL_APP_SCREENS.slice(10);
  let idx = 0;
  const loadNextBatch = () => {
    if (idx >= secondaryScreens.length) return;
    const batch = secondaryScreens.slice(idx, idx + 4);
    idx += 4;
    for (const screen of batch) {
      try {
        void screen.preload();
      } catch {
        // Ignore background preload failures
      }
    }
    scheduleIdleWork(loadNextBatch, 60);
  };

  scheduleIdleWork(loadNextBatch, 80);
}

function BootScreen({ error }: { error?: string | null }) {
  if (!error) return null;
  return (
    <div className="boot-screen">
      <div className="boot-intro" role="status" aria-live="polite">
        <div className="boot-logo-wrap">
          <BrandIcon size={76} className="boot-logo" />
        </div>
        <div className="boot-wordmark">TaskFlow</div>
        <p className="small danger-text" style={{ maxWidth: 380, marginTop: 12 }}>
          {error}
        </p>
        <Button icon="refresh" style={{ marginTop: 14 }} onClick={() => window.location.reload()}>
          إعادة المحاولة
        </Button>
      </div>
    </div>
  );
}

function BootingPlaceholder() {
  return (
    <div className="app-shell pe-fade-in" style={{ pointerEvents: 'none' }}>
      <aside className="sidebar" aria-hidden="true">
        <div className="sidebar-brand">
          <div className="brand-mark has-brand-icon">
            <BrandIcon size={38} />
          </div>
          <div className="sidebar-brand-copy grow">
            <div className="brand-name">TaskFlow</div>
            <div className="brand-sub">جاري فتح مساحة العمل…</div>
          </div>
        </div>
      </aside>
      <div className="main">
        <header className="topbar" style={{ minHeight: 'var(--topbar-height)' }} />
        <main className="content" style={{ padding: '24px 20px' }}>
          <DashboardSkeleton />
        </main>
      </div>
    </div>
  );
}

function RequireAuth() {
  const { snapshot, booting, error } = useAuth();
  const location = useLocation();
  if (booting) return <BootingPlaceholder />;
  if (!snapshot) {
    if (error) return <BootScreen error={error} />;
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (snapshot.platformSupportSession && (location.pathname.startsWith('/platform') || location.pathname === '/workspace')) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

function RequireCompany() {
  const { snapshot } = useAuth();
  if (!snapshot?.activeCompanyId) return <Navigate to="/workspace" replace />;
  return <Outlet />;
}

function RequirePlatform() {
  const { snapshot, canAny } = useAuth();
  const allowed =
    snapshot?.isPlatformOwner ||
    canAny([
      'platform.company.manage',
      'platform.user.manage',
      'platform.role.manage',
      'platform.settings.manage',
      'platform.module.manage',
      'platform.audit.view',
      'platform.owner.manage',
    ]);
  if (!allowed) return <Navigate to="/workspace" replace />;
  return <Outlet />;
}

function PermissionGate({
  permission,
  anyOf,
  children,
}: {
  permission?: PermissionKey;
  anyOf?: PermissionKey[];
  children: React.ReactNode;
}) {
  const { can, canAny } = useAuth();
  const allowed = permission ? can(permission) : anyOf ? canAny(anyOf) : true;
  if (!allowed) {
    return (
      <div className="page">
        <div className="card">
          <div className="card-body col" style={{ gap: 12, alignItems: 'center', textAlign: 'center', padding: 40 }}>
            <span style={{ color: 'var(--warning)' }}>
              <Icon name="shield" size={36} />
            </span>
            <h1 style={{ fontSize: 20 }}>لا تملك صلاحية الوصول</h1>
            <p className="small muted" style={{ maxWidth: 420 }}>
              الصلاحيات لا تُمنح من الواجهة. تواصل مع مسؤول المنشأة لمراجعة دورك ونطاقك. كل محاولة
              وصول إلى البيانات محمية على مستوى طبقة البيانات أيضًا.
            </p>
            <a href="/" className="btn btn-outline">
              العودة إلى لوحة المهام
            </a>
          </div>
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

function AppShell() {
  const { settings } = useAuth();
  const { applyBranding } = useTheme();

  // عند العودة من مساحة المنصة إلى الشركة نعيد تطبيق سمة الشركة النشطة،
  // لأن مساحة المنصة تطبّق سمة مستقلة.
  useEffect(() => {
    if (settings?.theme) applyBranding(settings.theme, undefined, 'company');
  }, [applyBranding, settings?.theme]);

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

function PlatformShell() {
  const { applyBranding } = useTheme();
  const { branding, theme } = usePlatformBranding();

  // مساحة المنصة لها سمة مستقلة. إن لم تكن هناك سمة محفوظة نستخدم الافتراضية
  // بدل وراثة سمة آخر شركة كانت نشطة.
  useEffect(() => {
    applyBranding(theme ?? TASKFLOW_PLATFORM_THEME, undefined, 'platform');
  }, [applyBranding, theme]);

  return (
    <AppLayout
      mode="platform"
      platformName={branding.productName}
      platformLogo={theme?.logoDataUrl ?? null}
    >
      <Outlet />
    </AppLayout>
  );
}

function NotFoundPage() {
  return (
    <div className="page">
      <div className="card">
        <div className="card-body col" style={{ gap: 12, alignItems: 'center', textAlign: 'center', padding: 48 }}>
          <Icon name="search" size={38} />
          <h1 style={{ fontSize: 22 }}>الصفحة غير موجودة</h1>
          <p className="small muted">قد يكون الرابط قديمًا أو محذوفًا.</p>
          <a href="/" className="btn btn-primary">
            لوحة المهام
          </a>
        </div>
      </div>
    </div>
  );
}

export function App() {
  const { gateway, snapshot, access, settings, booting } = useAuth();
  const actor = useActor();
  const activeCompanyId = snapshot?.activeCompanyId;

  useEffect(() => {
    if (!booting && typeof (window as any).__dismissTaskFlowIntro === 'function') {
      (window as any).__dismissTaskFlowIntro();
    }
    // Eagerly preload lookups and core app data into memory cache
    if (activeCompanyId) {
      preloadOrgLookups(gateway, activeCompanyId);
      preloadAllAppData(gateway, actor, settings, access);
    }
    // Silently and sequentially preload all application screens into browser cache
    triggerAutoPreloadAllScreens();
  }, [booting, gateway, activeCompanyId, actor, settings, access]);

  return (
    <Suspense fallback={<div className="route-loading-bar" />}>
      <Routes>
        <Route path="/index.html" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/bootstrap" element={<BootstrapPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/invite/:token" element={<InviteAcceptPage />} />

        <Route element={<RequireAuth />}>
          <Route path="/workspace" element={<WorkspacePage />} />
          <Route path="/register/complete" element={<RegisterCompletePage />} />

          <Route element={<RequirePlatform />}>
            <Route element={<PlatformShell />}>
              <Route path="/platform" element={<PlatformHomePage />} />
              <Route path="/platform/members" element={<PermissionGate permission="platform.user.manage"><PlatformMembersPage /></PermissionGate>} />
              <Route path="/platform/activity" element={<PermissionGate permission="platform.audit.view"><PlatformActivityPage /></PermissionGate>} />
              <Route
                path="/platform/companies"
                element={
                  <PermissionGate permission="platform.company.manage">
                    <CompaniesPage />
                  </PermissionGate>
                }
              />
              <Route
                path="/platform/settings"
                element={
                  <PermissionGate permission="platform.settings.manage">
                    <PlatformSettingsPage />
                  </PermissionGate>
                }
              />
              <Route
                path="/platform/billing"
                element={
                  <PermissionGate anyOf={['platform.settings.manage', 'platform.company.manage']}>
                    <PlatformBillingPage />
                  </PermissionGate>
                }
              />
              <Route path="/platform/profile" element={<ProfilePage />} />
              <Route path="/platform/preferences" element={<SettingsPage scope="platform" />} />
              <Route path="/platform/about" element={<AboutPage />} />
              <Route path="/platform/*" element={<NotFoundPage />} />
            </Route>
          </Route>

          <Route element={<RequireCompany />}>
            <Route element={<AppShell />}>
              <Route index element={<DashboardPage />} />
              <Route path="index.html" element={<Navigate to="/" replace />} />

              <Route path="/tasks/new" element={<PermissionGate permission="task.create"><TaskFormPage /></PermissionGate>} />
              <Route path="/tasks/:taskId" element={<TaskDetailsPage />} />
              <Route path="/tasks/:taskId/edit" element={<PermissionGate permission="task.update"><TaskFormPage /></PermissionGate>} />
              <Route path="/tasks/assigned" element={<TasksListPage view="assigned" />} />
              <Route path="/tasks/created" element={<TasksListPage view="created" />} />
              <Route path="/tasks/personal" element={<TasksListPage view="personal" />} />
              <Route path="/tasks/supervised" element={<TasksListPage view="supervised" />} />
              <Route path="/tasks/overdue" element={<TasksListPage view="overdue" />} />

              <Route path="/templates" element={<PermissionGate permission="task.template.manage"><TemplatesPage /></PermissionGate>} />
              <Route path="/templates/new" element={<PermissionGate permission="task.template.manage"><TemplateFormPage /></PermissionGate>} />
              <Route path="/templates/:templateId/edit" element={<PermissionGate permission="task.template.manage"><TemplateFormPage /></PermissionGate>} />

              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/bulletins" element={<BulletinsPage />} />
              <Route path="/manual" element={<StoreManualPage />} />
              <Route path="/staff-roles" element={<StaffRolesPage />} />
              <Route path="/media" element={<MediaHubPage />} />
              <Route path="/handover" element={<ShiftHandoverPage />} />
              <Route path="/leaderboard" element={<LeaderboardPage />} />
              <Route path="/assets" element={<AssetsPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/automations" element={<AutomationsPage />} />
              <Route path="/reports" element={<PermissionGate permission="report.view"><ReportsPage /></PermissionGate>} />
              <Route path="/profile" element={<CompanyProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/about" element={<AboutPage />} />

              <Route path="/admin/companies" element={<Navigate to="/platform/companies" replace />} />
              <Route path="/admin/structure" element={<Navigate to="/admin/branches" replace />} />
              <Route
                path="/admin/company"
                element={
                  <PermissionGate anyOf={['company.manage', 'company.settings.manage', 'task.category.manage']}>
                    <CompanySettingsPage />
                  </PermissionGate>
                }
              />
              <Route
                path="/admin/branches"
                element={
                  <PermissionGate anyOf={['branch.manage', 'company.manage']}>
                    <StructurePage initialTab="branches" />
                  </PermissionGate>
                }
              />
              <Route
                path="/admin/brands"
                element={
                  <PermissionGate anyOf={['brand.manage', 'company.manage']}>
                    <StructurePage initialTab="brands" />
                  </PermissionGate>
                }
              />
              <Route path="/admin/users" element={<PermissionGate anyOf={['user.view', 'membership.manage']}><UsersPage /></PermissionGate>} />
              <Route path="/admin/roles" element={<PermissionGate permission="role.view"><RolesPage /></PermissionGate>} />
              <Route path="/admin/roles/new" element={<PermissionGate permission="role.manage"><RoleEditorPage /></PermissionGate>} />
              <Route path="/admin/roles/:roleId" element={<PermissionGate permission="role.view"><RoleEditorPage /></PermissionGate>} />
              <Route path="/admin/supervision" element={<PermissionGate permission="supervision.manage"><SupervisionPage /></PermissionGate>} />
              <Route path="/admin/categories" element={<PermissionGate permission="task.category.manage"><CategoriesPage /></PermissionGate>} />
              <Route path="/admin/theming" element={<PermissionGate permission="company.settings.manage"><ThemingPage /></PermissionGate>} />
              <Route
                path="/admin/retention"
                element={
                  <PermissionGate anyOf={['attachment.retention.manage', 'company.settings.manage', 'company.manage']}>
                    <RetentionPage />
                  </PermissionGate>
                }
              />
              <Route path="/admin/audit" element={<PermissionGate anyOf={['audit.view', 'platform.audit.view']}><AuditPage /></PermissionGate>} />

              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
